// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The sixteen validation classes, sorted (M9-25, 11 §5, 04 §16).
//
// *"Author clicks Publish → Client-side validation (all 16 classes) — **fast fail, inline**."*
//
// **The validator already answers; what it does not do is say which question failed.** It returns a
// flat list of issues, and a flat list is what an author reads as "my machine is broken" rather than
// as "the rules reference something that is not there". 04 §16 groups the same work into classes —
// Schema, Bounds, the five geometry passes, Layer integrity, the four rules passes, Display, Art,
// Audio and Playability — and those groups are how an author thinks about a machine, so this sorts
// the issues into them.
//
// **The one property that makes it worth having is total coverage.** An issue code that belongs to
// no class appears nowhere: the panel shows sixteen green rows over a machine the validator refuses,
// and the author believes the panel. So `validation-classes.test.ts` reads the core's own source,
// collects every code `ctx.error`, `ctx.warn` and the geometry and termination passes can emit, and
// asserts that each is owned by exactly one class here. A code added to the core without a home
// fails that test rather than disappearing.
//
// **Playability is the soak, and is shown as what it is.** 04's own note: *"Fifteen have a passing
// and a deliberately failing fixture … Playability is `npm run soak`."* It cannot run in the
// keystroke path — it is minutes of simulation — so the panel names it and points at M9-21's button
// rather than showing a green row nobody earned.
//
// Pure: no DOM. It takes a machine and returns a sorted report.

import {
  analyseWithReachability,
  BUDGET_CODE,
  overBudget,
  validateMachine,
  type MachineDefinition,
  type ValidationIssue,
} from '../../core/ts/machine'
import { DOCUMENT_BUDGET } from '@server/publishValidation'

export interface ValidationClass {
  /** 04 §16's own numbering, as the fixture table uses it. Playability is 16. */
  readonly number: number
  readonly title: string
  /** What this class refuses, in the words an author would use. */
  readonly rejects: string
  /** Every issue code this class owns. Held to the core's source by a test. */
  readonly codes: readonly string[]
  /**
   * Whether it runs while the author types.
   *
   * The soak does not, and neither would a check that took a second. Everything here except
   * Playability is milliseconds — the two flood passes are the expensive ones, at a rasterisation
   * each, which is why 04 has them off by default and the Builder pays for them anyway.
   */
  readonly live: boolean
}

/**
 * The classes, in 04 §16's order.
 *
 * **Seventeen rows, sixteen numbers.** 04's table lists the flipper outboard gap as a row of its
 * own and the implemented fixture table folds it into the flipper geometry work, so the numbering
 * runs 1–15 plus Playability. Both are kept: the row exists because an author needs to be told
 * *that* their inlane guide is a ball's width from the bat, and the number matches the code so a
 * failure here and a failure in `validationCases.ts` are the same failure.
 */
export const VALIDATION_CLASSES: readonly ValidationClass[] = [
  {
    number: 1,
    title: 'Schema',
    rejects: 'Anything that is not a machine: a missing field, an unknown one, a number where a string belongs.',
    live: true,
    codes: [
      'not-an-object', 'not-an-array', 'not-a-string', 'not-a-number', 'not-an-integer',
      'not-a-boolean', 'not-a-point', 'not-a-point3', 'not-a-range', 'not-a-colour',
      'not-in-options', 'not-in-vocabulary', 'not-semver', 'missing-field', 'unknown-field',
      'missing-id', 'duplicate-id', 'too-short', 'format-version-unsupported', 'no-playfield',
    ],
  },
  {
    number: 2,
    title: 'Bounds',
    rejects: 'A number outside its declared range — an incline under 5° or over 8°, a material past its cap.',
    live: true,
    codes: [
      'out-of-range', 'unknown-material', 'balls-exceed-trough', 'glass-below-ball',
      'outlane-scatter-unset', 'flashes-in-the-seizure-band',
    ],
  },
  {
    number: 3,
    title: 'Geometry — containment',
    rejects: 'A collider outside the playfield, or one crossing the cabinet.',
    live: true,
    codes: [
      'outside-playfield', 'region-off-playfield', 'inside-out-region', 'inverted-rect',
      'degenerate-face', 'degenerate-segment', 'degenerate-arc', 'zero-vector',
      'normal-not-unit', 'normal-not-perpendicular',
    ],
  },
  {
    number: 4,
    title: 'Geometry — overlap',
    rejects: 'Two static colliders interpenetrating.',
    live: true,
    codes: ['colliders-overlap', 'eject-blocked'],
  },
  {
    number: 5,
    title: 'Geometry — flipper crossing',
    rejects: 'A flipper pair that interpenetrates (PH-054), and one with no centre drain between them.',
    live: true,
    codes: ['flippers-interpenetrate', 'no-centre-drain', 'no-drain'],
  },
  {
    number: 5,
    title: 'Geometry — flipper outboard gap',
    rejects:
      'An inlane guide that ends more than a ball from the flipper base — every ball fed down the inlane passes the bat on the outside and drains, and nothing else can see it.',
    live: true,
    codes: ['flipper-outboard-gap'],
  },
  {
    number: 6,
    title: 'Geometry — trap',
    rejects: 'A pocket the ball can enter and not leave, found by flood fill at ball radius.',
    live: true,
    codes: ['ball-trap', 'no-ball-entry'],
  },
  {
    number: 7,
    title: 'Geometry — reachability',
    rejects: 'Nothing — it warns. A scoring device no flipper can reach is legal and pointless.',
    live: true,
    codes: ['device-unreachable'],
  },
  {
    number: 8,
    title: 'Layer integrity',
    rejects: 'A ramp exit that names nothing, a loop of exits, and a ramp the ball cannot climb.',
    live: true,
    codes: [
      'raised-plane-needs-bounds', 'unknown-layer', 'layer-exit-dead-end', 'layer-exit-cycle', 'layer-hits-glass',
      'ramp-not-climbable',
    ],
  },
  {
    number: 9,
    title: 'References',
    rejects:
      'A rule naming a switch, coil, lamp, var, timer, mode, scene or cue that does not exist, ' +
      'or a rule or collider naming an operator setting that does not — and, as a warning, a ' +
      'lamp no rule, group or device ever names.',
    live: true,
    codes: [
      'unknown-switch', 'unknown-coil', 'unknown-lamp', 'unknown-lamp-group', 'unknown-var',
      'unknown-timer', 'unknown-mode', 'unknown-scene', 'unknown-cue', 'unknown-speech',
      'unknown-device', 'unknown-region', 'unknown-drop-target', 'unknown-trigger',
      'unknown-action', 'unknown-operator', 'unknown-art-op', 'unknown-display-op',
      'action-malformed', 'op-malformed', 'expression-malformed', 'expression-string',
      'stages-out-of-order',
      // **The inverse of `unknown-lamp`, and it belongs in the same class** (M9-40): both are
      // about a name and a thing failing to meet. One is a rule pointing at a lamp that is not
      // there; this is a lamp no rule, group or device ever points at — dark for the life of
      // the machine, and the only sign is a bulb that never comes on.
      'lamp-never-lit',
      // **A rule naming an operator setting the machine does not declare** (M9-35): the same
      // class of mistake as `unknown-cue`, one block further out. `enableBallSave: "operator"`
      // asks 04 §15 for a number, and a machine with no `ballSaveSeconds` has none to give —
      // which fell through to a nought-second ball save that reads, in the document, as a save.
      'unknown-setting',
      // **And a collider that names one** (M9-36). `adjustedBy` is how 04 §6 answers the question
      // 04 §15 does not — which colliders a setting moves, and by how much — so a post can now
      // dangle against the operator menu exactly as a rule can. `adjustment-incomplete` is the
      // silent half: an offset missing for a declared option moves nothing when a player picks
      // that option, while `settingsHash` still folds the choice into the leaderboard key.
      'adjustment-incomplete',
    ],
  },
  {
    number: 10,
    title: 'Rules — termination',
    rejects: 'A rulesheet whose event graph cannot be proved to stop inside depth 8.',
    live: true,
    codes: ['rule-cycle', 'rule-cycle-guarded', 'rule-cascade-too-deep'],
  },
  {
    number: 11,
    title: 'Rules — budget',
    rejects: 'An expression deeper than 16 or larger than 256 nodes.',
    live: true,
    codes: ['expression-too-deep', 'expression-too-large'],
  },
  {
    number: 12,
    title: 'Rules — reachability',
    rejects: 'A mode nothing can start, and a mode nothing can end. Both are features nobody reaches.',
    live: true,
    codes: ['mode-unreachable', 'mode-cannot-end'],
  },
  {
    number: 13,
    title: 'Display',
    rejects: 'A scene a rule shows that is not declared, and a frame op outside the vocabulary.',
    live: true,
    codes: ['display-over-budget'],
  },
  {
    number: 14,
    title: 'Art',
    rejects: 'An op outside the vocabulary, a missing seed, and more drawing than the budget allows.',
    live: true,
    codes: ['art-over-budget'],
  },
  {
    number: 15,
    title: 'Audio',
    rejects: 'A cue a rule plays that is not declared, and a synth name with no recipe.',
    live: true,
    codes: [],
  },
  {
    // **Seventeen, because this is not one of 04 §16's classes.** The budgets are 11 §8's, and they
    // are not format rules at all — a machine over budget is well-formed. Numbering it 16 would put
    // it on top of Playability and imply the format refuses it, which is exactly the confusion the
    // number is there to prevent.
    number: 17,
    title: 'Budgets',
    rejects:
      '11 §8\'s limits — 120 devices, 400 colliders, 160 lamps, 400 rules, 24 modes, six balls, 64 scenes, 120 cues and a 512 KB document — so that a published machine cannot degrade anyone\'s device.',
    live: true,
    codes: [BUDGET_CODE],
  },
  {
    number: 16,
    title: 'Playability',
    rejects:
      'A machine where the ball gets stuck. Ten thousand balls must drain through legitimate outlanes without a single ball-search teleport (PH-091).',
    // Minutes of simulation, not milliseconds. It is the Soak button, and saying so is better than
    // a green row nobody earned.
    live: false,
    codes: [],
  },
]

export type ClassStatus = 'pass' | 'warn' | 'fail' | 'not-run'

export interface ClassResult {
  readonly cls: ValidationClass
  readonly status: ClassStatus
  readonly issues: readonly ValidationIssue[]
}

export interface ClassReport {
  readonly classes: readonly ClassResult[]
  readonly errors: number
  readonly warnings: number
  /**
   * Issues no class claims.
   *
   * **Shown rather than dropped.** A test keeps this empty, but the panel would rather say "and
   * three other problems" than let an author read sixteen green rows over a machine that will not
   * publish. A silent drop here is the one way this file can lie.
   */
  readonly unsorted: readonly ValidationIssue[]
}

/** Which class owns a code, or nothing. */
export function classOf(code: string): ValidationClass | undefined {
  return VALIDATION_CLASSES.find((cls) => cls.codes.includes(code))
}

/**
 * The one budget in this class's own list that it never checked (M12-44).
 *
 * **Class 17 has promised "a 512 KB document" since it was written, and measured nine counted
 * things.** `overBudget` is the core's table — devices, colliders, lamps, rules, modes, balls, art
 * ops, scenes, cues — and the document's size is not in it, because the core cannot weigh bytes:
 * `overDocumentBudget`'s own header says measuring needs `TextEncoder`, which the purity rule
 * refuses. So the class listed the limit in its `rejects` sentence, the panel drew the class, and a
 * machine over the cap passed it.
 *
 * That matters most for the things nothing else counts. `BUDGETS` bounds nine kinds; `switches`,
 * `coils`, `regions`, `layers`, `vars`, `timers`, `toys` and every point and spline array have no
 * count limit anywhere, and 29 of the schema's 35 arrays carry no `maxItems`. This cap is the only
 * thing bounding them — which is why it is the budget an author actually meets, and why an author
 * met it at `save_version` with a 413 rather than here.
 *
 * **Length, not order.** The server weighs `JSON.stringify(definition)` at all three of its call
 * sites, and `canonicalDefinitionBytes` is that same call over a key-sorted copy — sorting changes
 * the order of the bytes and never how many there are, which is why this agrees with the server
 * without importing its serialiser into the browser bundle.
 */
function documentOverBudget(definition: MachineDefinition): ValidationIssue[] {
  const bytes = new TextEncoder().encode(JSON.stringify(definition)).length
  if (bytes <= DOCUMENT_BUDGET) return []
  return [
    {
      path: 'document',
      code: BUDGET_CODE,
      message: `${bytes} bytes exceeds the ${DOCUMENT_BUDGET} a definition may be, by ${bytes - DOCUMENT_BUDGET} bytes over. The server refuses it before it parses it, so it cannot be saved or published however well it validates. Nothing counts switches, coils, regions, layers, variables, timers, toys or the points in a spline — this cap is what bounds them.`,
    },
  ]
}

/**
 * Every class, with what the validator said about it.
 *
 * **The flood passes are on.** 04 §16 has them off by default because they rasterise the playfield
 * at ball radius, and names the Builder as the caller that pays for it: a trap is precisely what an
 * author cannot see by looking, and finding it at publish is finding it after the work.
 */
export function classifyMachine(definition: MachineDefinition): ClassReport {
  const report = validateMachine(definition, { flood: true })
  // The geometry pass runs inside `validateMachine` too, but its issues carry their own codes and
  // this asks for them again rather than filtering: `analyseWithReachability` is what the canvas
  // already draws, so the panel and the overlay cannot disagree about what is wrong.
  const geometry = analyseWithReachability(definition).issues
  // **The budgets, at save** (M9-30). The same table the server refuses on and the same one the
  // client will refuse to load, so an author sees the number here rather than at publish.
  const budgets: ValidationIssue[] = [
    ...overBudget(definition).map((overrun) => ({
      path: overrun.path,
      code: BUDGET_CODE,
      message: overrun.message,
    })),
    ...documentOverBudget(definition),
  ]
  const all: ValidationIssue[] = [
    ...report.errors,
    ...budgets,
    ...report.warnings,
    ...geometry
      .filter((issue) => !report.errors.some((seen) => same(seen, issue)))
      .filter((issue) => !report.warnings.some((seen) => same(seen, issue)))
      .map((issue) => ({ path: issue.path, code: issue.code, message: issue.message })),
  ]

  // Sorted through `classOf`, which is the one answer to "which class owns this code" — the first
  // version asked the question a second way here, and two answers to one question is how a panel
  // and its own test come to disagree.
  const classes = VALIDATION_CLASSES.map((cls) => {
    const mine = all.filter((issue) => classOf(issue.code) === cls)
    // A budget overrun is a refusal even though the *validator* did not raise it: 11 §8's limits
    // are not format rules, so `report.errors` does not carry them and a status read from that
    // alone would show the Budgets row as a warning over a machine that will not publish.
    const fails = mine.some(
      (issue) =>
        issue.code === BUDGET_CODE || report.errors.some((error) => same(error, issue)),
    )
    return {
      cls,
      status: !cls.live ? ('not-run' as const) : fails ? ('fail' as const) : mine.length > 0 ? ('warn' as const) : ('pass' as const),
      issues: mine,
    }
  })

  return {
    classes,
    errors: report.errors.length + budgets.length,
    warnings: report.warnings.length,
    unsorted: all.filter((issue) => classOf(issue.code) === undefined),
  }
}

function same(a: ValidationIssue, b: { path: string; code: string }): boolean {
  return a.code === b.code && a.path === b.path
}

/**
 * The device or layer an issue is about, if it is about one.
 *
 * **This is what "inline" means.** An issue that names `devices[7].length` is an issue about a
 * flipper the author can see, and a panel that made them count to seven is a panel that reports
 * rather than helps.
 */
export function subjectOf(issue: { readonly path: string }): { kind: 'device' | 'layer'; index: number } | undefined {
  const device = /^devices\[(\d+)\]/u.exec(issue.path)
  if (device !== null) return { kind: 'device', index: Number(device[1]) }
  const layer = /^layers\[(\d+)\]/u.exec(issue.path)
  if (layer !== null) return { kind: 'layer', index: Number(layer[1]) }
  return undefined
}
